/**
 * io-product-scroll.js
 * Handles arrow-button scrolling and custom scrollbar sync
 * for the Scrollable Product Row section.
 * Mirrors the logic of io-collection-scroll.js.
 */

(function () {
  function initProductScroll(section) {
    const track          = section.querySelector('.product-row__track');
    const thumb          = section.querySelector('.custom-scrollbar-thumb');
    const scrollbarTrack = section.querySelector('.custom-scrollbar-track');
    const btnLeft        = section.querySelector('.scroll-btn.left');
    const btnRight       = section.querySelector('.scroll-btn.right');

    if (!track) return;

    // How far to jump per arrow click — two card widths + gap
    function getScrollStep() {
      const card = track.querySelector('.product-card');
      if (!card) return 280;
      return card.offsetWidth + 16; // card width + gap
    }

    /*
     * FIX — Prevent the back button from scrolling past the start (or
     * the forward button past the end) when the smooth-scroll animation
     * hasn't finished yet.
     *
     * Root cause: scrollBy is asynchronous. scrollLeft hasn't settled
     * when the next click fires, so updateButtons() sees a stale value
     * and doesn't disable the button in time.
     *
     * Solution:
     *   1. Use scrollTo with a clamped target instead of scrollBy, so the
     *      destination is always within [0, maxScroll] regardless of timing.
     *   2. Set isScrolling = true on click and block further clicks until
     *      the animation ends (scrollend event or 400ms fallback timeout).
     */
    let isScrolling = false;
    let scrollTimer = null;

    function scrollToPosition(targetLeft) {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const clamped   = Math.max(0, Math.min(targetLeft, maxScroll));

      if (Math.abs(track.scrollLeft - clamped) < 2) return;

      isScrolling = true;
      track.scrollTo({ left: clamped, behavior: 'smooth' });

      // Clear any previous fallback timer
      clearTimeout(scrollTimer);

      if ('onscrollend' in track) {
        // Modern browsers — resolves exactly when scroll animation ends
        track.addEventListener('scrollend', function onEnd() {
          track.removeEventListener('scrollend', onEnd);
          isScrolling = false;
          updateButtons();
        });
      } else {
        // Safari / older browsers — conservative 400ms timeout
        scrollTimer = setTimeout(() => {
          isScrolling = false;
          updateButtons();
        }, 400);
      }
    }

    // ----- Arrow buttons -----
    btnLeft && btnLeft.addEventListener('click', () => {
      if (isScrolling) return;
      scrollToPosition(track.scrollLeft - getScrollStep() * 2);
    });

    btnRight && btnRight.addEventListener('click', () => {
      if (isScrolling) return;
      scrollToPosition(track.scrollLeft + getScrollStep() * 2);
    });

    // ----- Custom scrollbar thumb -----
    function updateThumb() {
      const scrollable = track.scrollWidth - track.clientWidth;
      if (scrollable <= 0 || !thumb || !scrollbarTrack) return;

      const progress   = track.scrollLeft / scrollable;
      const trackWidth = scrollbarTrack.offsetWidth;
      const thumbWidth = Math.max(40, (track.clientWidth / track.scrollWidth) * trackWidth);

      thumb.style.width = thumbWidth + 'px';
      thumb.style.left  = (progress * (trackWidth - thumbWidth)) + 'px';
    }

    // ----- Disabled state for buttons -----
    function updateButtons() {
      if (!btnLeft || !btnRight) return;
      btnLeft.disabled  = track.scrollLeft <= 0;
      btnRight.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }

    track.addEventListener('scroll', () => {
      updateThumb();
      updateButtons();
    }, { passive: true });

    // Draggable scrollbar thumb
    if (thumb && scrollbarTrack) {
      let dragging = false;
      let startX, startLeft;

      thumb.addEventListener('mousedown', (e) => {
        dragging  = true;
        startX    = e.clientX;
        startLeft = track.scrollLeft;
        e.preventDefault();
      });

      document.addEventListener('mousemove', (e) => {
        if (!dragging) return;
        const trackWidth = scrollbarTrack.offsetWidth;
        const thumbWidth = thumb.offsetWidth;
        const scrollable = track.scrollWidth - track.clientWidth;
        const dx         = e.clientX - startX;
        const ratio      = dx / (trackWidth - thumbWidth);
        track.scrollLeft = startLeft + ratio * scrollable;
      });

      document.addEventListener('mouseup', () => { dragging = false; });
    }

    // Initial state
    updateThumb();
    updateButtons();

    // Recalculate on resize
    const ro = new ResizeObserver(() => { updateThumb(); updateButtons(); });
    ro.observe(track);
  }

  // Boot for all instances on the page
  function boot() {
    document.querySelectorAll('[data-section-type="product-scroll"]').forEach(initProductScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor support — re-init when section is selected/reloaded
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-section-type="product-scroll"]');
    if (section) initProductScroll(section);
  });
})();